import React from 'react';
import styled from '@emotion/styled';
import { Divider, Grid, Skeleton } from '@mui/material';
import AccordionSimple from '../../_common/AccordionSimple';
import TabHeading from '../../_common/Tabs/TabHeading';
import { selectActiveVersion, selectTitleLoading } from '@/redux/selectors/titleSelectors';
import { useAppSelector } from '@/redux/hooks';
import SizePreservingLoader from '@/components/_common/SizePreservingLoader/SizePreservingLoader';
import { isActiveVersionAVersion } from '@/redux/slices/slice_types';

const TitleDetailExtraMetadata = () => {
  const selectedVersion = useAppSelector(selectActiveVersion);
  const loadingTitle = useAppSelector(selectTitleLoading);
  const objectKeys: string[] = [];

  if (!isActiveVersionAVersion(selectedVersion) || !selectedVersion.meta) return null;

  const nonObjectKeys = Object.keys(selectedVersion.meta)
    .filter((key) => {
      if (typeof selectedVersion.meta?.[key] !== 'object') {
        return true;
      } else {
        objectKeys.push(key);
        return false;
      }
    })
    .sort();

  // objectKeys.forEach((key) => console.log(`"${key}" is an object and cannot be displayed in this table`));

  if (!nonObjectKeys.length) return null;

  return (
    <>
      <Divider sx={{ mt: 5, mb: 5 }} />
      <ExtraMetadata container>
        <AccordionSimple title={<TabHeading>Extended Metadata</TabHeading>}>
          <SizePreservingLoader isLoading={loadingTitle} loader={<SkeletonLoader rowsNum={nonObjectKeys.length} />}>
            <AltIdsList onClick={(e) => e.stopPropagation()}>
              <tbody>
                {nonObjectKeys.map((key) => (
                  <TableRow key={`${key}+${selectedVersion.meta?.[key]}`} label={key} value={selectedVersion.meta?.[key]} />
                ))}
              </tbody>
            </AltIdsList>
          </SizePreservingLoader>
        </AccordionSimple>
      </ExtraMetadata>
    </>
  );
};

export default TitleDetailExtraMetadata;

interface SkeletonLoaderProps {
  rowsNum: number;
}
const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ rowsNum }) => (
  <AltIdsList>
    <tbody>
      {Array.from({ length: rowsNum }).map((_, i) => (
        <tr key={i}>
          <td>
            <Skeleton sx={{ minWidth: 400 }} />
          </td>
        </tr>
      ))}
    </tbody>
  </AltIdsList>
);

const TableRow = ({ label, value }: { label: string; value: any }) => {
  const prettifyArrayString = (val: string) => {
    let display;

    try {
      if (label === 'length') {
        display = value.replace(/\[(\d+)\] \[(\d+)\]/, '$1:$2');
      } else {
        display = (
          <>
            <br />
            {JSON.parse(val)
              .map((entry: any) => JSON.stringify(entry, null).replace(/({|:|\})/g, ' $1 '))
              .join(',\n')}
            <br />
          </>
        );
      }
    } catch {
      console.error('Could not display this value: ', label, value);
      display = value;
    }

    return <StyledPreformattedText>{display}</StyledPreformattedText>;
  };

  const prettifyObjectString = (val: string) => {
    const parsed = JSON.parse(val);
    return <StyledPreformattedText>{JSON.stringify(parsed, null, '  ')}</StyledPreformattedText>;
  };

  const getValue = (val: any) => {
    if (typeof val !== 'string') return `${val}`;

    switch (val[0]) {
      case '[':
        return prettifyArrayString(val);
      case '{':
        return prettifyObjectString(val);
      default:
        return val;
    }
  };

  return (
    <tr>
      <td>
        <MetaKey>{label}</MetaKey>
      </td>
      <td>{getValue(value)}</td>
    </tr>
  );
};

const MetaKey = styled.strong`
  opacity: 0.6;
`;

const StyledPreformattedText = styled.pre`
  max-height: 150px;
  overflow-y: auto;
`;

const ExtraMetadata = styled(Grid)`
  width: 100%;

  & .MuiBox-root {
    padding-bottom: 0px;
    margin-bottom: 0px;
  }

  & .MuiAccordion-root {
    width: 100%;
  }
`;

const AltIdsList = styled.table`
  width: 100%;
  margin-left: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};

  & tr th,
  tr td {
    padding: 10px;
    margin: 0px;
    text-align: left;
  }

  & tr th {
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    opacity: 0.5;
  }

  & tr:nth-of-type(odd) {
    background-color: ${({ theme }) => theme.palette.primary.main + '10'};
  }
`;
